import { Button, ConfigProvider, DatePicker } from 'antd';
import localeEn from 'antd/locale/en_US';
import localeFa from 'antd/locale/fa_IR';
import classNames from 'classnames';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/fa';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekday from 'dayjs/plugin/weekday';
import jMoment from 'jalali-moment';
import jalaliday from 'jalaliday';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { GrFormNext, GrFormPrevious } from 'react-icons/gr';
import {
  SORT_ALL,
  SORT_DAILY,
  SORT_MONTHLY,
  SORT_REALTIME,
  SORT_YEARLY,
} from '../../../utils/Utils';
import useCalendar from '../../../hooks/useCalendar';
import { getNow, getFixedDay } from '../../../utils/fixedNow';
import { displayToCanonicalIso } from './helper';
import { dir } from 'i18n';

function getPickerForSort({ sort, powerGenerationPage = false }) {
  if (powerGenerationPage) {
    if (sort === SORT_YEARLY) return 'month';
    return 'date';
  }
  switch (sort) {
    case SORT_MONTHLY:
      return 'month';
    case SORT_DAILY:
      return 'date';
    case SORT_YEARLY:
      return 'year';
    case SORT_REALTIME:
      return 'date';
    default:
      return 'date';
  }
}

const shouldApplyJalaliFix = (isJalali, sort) =>
  Boolean(isJalali) && [SORT_REALTIME, SORT_DAILY, SORT_MONTHLY].includes(sort);

const applyJalaliDayFix = (start, end, isJalali, sort) => {
  if (!shouldApplyJalaliFix(isJalali, sort)) return { start, end };
  return {
    // start: sort === SORT_REALTIME ? start.subtract(1, 'day') : start,
    // end: end.subtract(1, 'day'),
    start: start,
    end: end,
  };
};

dayjs.locale('es'); // Set to Spanish
dayjs.extend(jalaliday);
dayjs.locale('fa');
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekday);

function DateFilter({
  alwaysVisible,
  ignoreRealtimeMode,
  state,
  setState,
  disable,
  pending = false,
  next_previous_btn,
  logFilter,
  historyFilter,
  PowerGeneration_Page,
  dashboardDatePickWidth = false,
}) {
  const isJalali = useCalendar();

  const creationDate = localStorage.getItem('creationDate');
  const [weeklyMode, setWeeklyMode] = useState(false);
  const handleRangedMode = state.sort == SORT_REALTIME && !ignoreRealtimeMode;

  useEffect(() => {
    if (!PowerGeneration_Page) return;
    if (isJalali === null) return;
    const dw = state?.dateWeek;
    const isArray = Array.isArray(dw);
    const isDayjsSingle = dayjs.isDayjs(dw);
    const isValidRangeArray =
      isArray &&
      dw.length === 2 &&
      dayjs.isDayjs(dw[0]) &&
      dayjs.isDayjs(dw[1]) &&
      !dw[0].isSame(dw[1]);

    if (isValidRangeArray) return;

    // const now = isJalali ? dayjs().calendar('jalali') : dayjs();
    const rawNow = getNow();
    const now = isJalali ? rawNow.calendar('jalali') : rawNow;
    let start;
    let end;
    switch (state.sort) {
      case SORT_REALTIME:
        start = now.startOf('day');
        end = now.endOf('day');
        break;
      case SORT_DAILY:
        start = now.startOf('week');
        end = now.endOf('week');
        break;
      case SORT_MONTHLY:
        start = now.startOf('month');
        end = now.endOf('day');
        break;
      case SORT_YEARLY:
        start = now.startOf('year');
        end = now.endOf('year');
        break;
      default:
        start = now.startOf('day');
        end = now.endOf('day');
    }
    const { start: adjStart, end: adjEnd } = applyJalaliDayFix(
      start,
      end,
      isJalali,
      state.sort,
    );

    const storedStart = isJalali ? adjStart.calendar('jalali') : adjStart;
    const storedEnd = isJalali ? adjEnd.calendar('jalali') : adjEnd;

    setState((prev) => ({
      ...prev,
      dateWeek: [storedStart, storedEnd],
      dateValue: [
        storedStart.format('YYYY-MM-DD'),
        storedEnd.format('YYYY-MM-DD'),
      ],
      dateString: [
        storedStart.format('YYYY-MM-DD'),
        storedEnd.format('YYYY-MM-DD'),
      ],
      startDate: storedStart.format('YYYY-MM-DDT00:00:00.000Z'),
      endDate: storedEnd.format('YYYY-MM-DDT23:59:59.000Z'),
    }));
  }, [PowerGeneration_Page, state.sort, isJalali]);

  const getDateWeekDayjs = () => {
    const dw = state?.dateWeek;
    if (!dw) return getNow();
    if (Array.isArray(dw)) {
      const pick = dw.length > 1 ? dw[1] : dw[0];
      return dayjs(
        moment.isMoment(pick)
          ? pick.toISOString()
          : pick && pick.toISOString
          ? pick.toISOString()
          : pick,
      );
    }
    if (dayjs.isDayjs(dw)) return dw;
    if (moment.isMoment(dw)) return dayjs(dw.toISOString());
    return dayjs(dw);
  };

  const toRange = (date) => {
    if (!date) return null;
    if (Array.isArray(date)) return date;
    if (dayjs.isDayjs(date)) return [date, date];
    return null;
  };

  const normalizeDateString = (ds) => {
    if (!ds) return null;
    if (Array.isArray(ds)) return ds;
    return [ds];
  };
  // useEffect(() => {
  //   console.log(state.dateWeek);
  // }, [state.dateWeek]);

  const handle_realtimeDateChange = (date, dateString) => {
    const isRanged = handleRangedMode || PowerGeneration_Page;
    const range = isRanged ? toRange(date) : Array.isArray(date) ? date : null;
    const dsArr = normalizeDateString(dateString);

    const startDay = range ? range[0] : dayjs.isDayjs(date) ? date : null;
    const endDay = range ? range[1] : dayjs.isDayjs(date) ? date : null;

    if (!startDay || !endDay) {
      const now = dayjs();
      const startIso = displayToCanonicalIso(now, isJalali, 'start');
      const endIso = displayToCanonicalIso(now, isJalali, 'end');
      setState({
        ...state,
        dateValue: dsArr || null,
        dateString: dsArr
          ? dsArr.map((d) => (Array.isArray(d) ? d[0] : d))
          : null,
        dateWeek: PowerGeneration_Page
          ? [now.startOf('day'), now.endOf('day')]
          : now,
        startDate: startIso,
        endDate: endIso,
      });
      return;
    }

    const startIso = displayToCanonicalIso(startDay, isJalali, 'start');
    const endIso = displayToCanonicalIso(endDay, isJalali, 'end');

    setState({
      ...state,
      dateValue:
        dsArr ||
        (isRanged
          ? [startDay.format('YYYY-MM-DD'), endDay.format('YYYY-MM-DD')]
          : [startDay.format('YYYY-MM-DD')]),
      dateString: isRanged
        ? [startDay.format('YYYY-MM-DD'), endDay.format('YYYY-MM-DD')]
        : [startDay.format('YYYY-MM-DD')],
      dateWeek: isRanged ? [startDay, endDay] : startDay,
      startDate: startIso,
      endDate: endIso,
    });
  };

  const handle_dailyDateChange = (date, dateString) => {
    const isPG = PowerGeneration_Page;
    const range = toRange(date);
    if (isPG && range) {
      const [s, e] = range;
      setState({
        ...state,
        dateValue: normalizeDateString(dateString) || [
          s.format('YYYY-MM-DD'),
          e.format('YYYY-MM-DD'),
        ],
        dateWeek: [s, e],
        startDate: s.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
        endDate: e.endOf('day').format('YYYY-MM-DDT23:59:59.000+03:30'),
      });
      return;
    }

    const start = date?.startOf('week') || dayjs().startOf('week');
    const end = date?.endOf('week') || dayjs().endOf('week');

    setState({
      ...state,
      dateValue: normalizeDateString(dateString) || [
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
      ],
      dateWeek: isPG ? [start, end] : date,
      startDate: start.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
      endDate: end.endOf('day').format('YYYY-MM-DDT23:59:59.000+03:30'),
    });
  };

  const handle_monthlyDateChange = (date, dateString) => {
    const isPG = PowerGeneration_Page;
    const range = toRange(date);
    if (isPG && range) {
      const [s, e] = range;
      setState({
        ...state,
        dateValue: normalizeDateString(dateString) || [
          s.format('YYYY-MM-DD'),
          e.format('YYYY-MM-DD'),
        ],
        dateWeek: [s, e],
        startDate: s.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
        endDate: e.endOf('day').format('YYYY-MM-DDT23:59:59.000+03:30'),
      });
      return;
    }

    const start = date?.startOf('month') || dayjs().startOf('month');
    // const isThisMonth = dayjs(date).isBetween(
    //   dayjs().startOf('month'),
    //   dayjs().endOf('month'),
    //   null,
    //   '[]',
    // );
    const now = getNow();
    const isThisMonth = dayjs(date).isBetween(
      now.startOf('month'),
      now.endOf('month'),
      null,
      '[]',
    );
    const end = isThisMonth ? dayjs() : date?.endOf('month');

    setState({
      ...state,
      dateValue: normalizeDateString(dateString) || [
        start.format('YYYY-MM-DD'),
        (end || start).format('YYYY-MM-DD'),
      ],
      dateWeek: isPG ? [start, end] : date,
      startDate: start.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
      endDate: (end || start)
        .endOf('day')
        .format('YYYY-MM-DDT23:59:59.000+03:30'),
    });
  };

  const handle_yearlyDateChange = (date, dateString) => {
    const isPG = PowerGeneration_Page;
    const range = toRange(date);
    if (isPG && range) {
      const [s, e] = range;
      setState({
        ...state,
        dateValue: normalizeDateString(dateString) || [
          s.format('YYYY-MM-DD'),
          e.format('YYYY-MM-DD'),
        ],
        dateWeek: [s, e],
        startDate: s.startOf('day').format('YYYY-MM-DDT00:00:00.000'),
        endDate: e.endOf('month').format('YYYY-MM-DDT23:59:59.000'),
      });
      return;
    }

    const start = date?.startOf('year') || dayjs().startOf('year');
    const end = date?.endOf('year') || dayjs().endOf('year');

    setState({
      ...state,
      dateValue: normalizeDateString(dateString) || [
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
      ],
      dateWeek: isPG ? [start, end] : date,
      startDate: start.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
      endDate: end.endOf('day').format('YYYY-MM-DDT23:59:59.000+03:30'),
    });
  };

  const handle_simpleDateChange = (date, dateString) => {
    if (!date) {
      setState({
        ...state,
        dateValue: null,
        dateString: null,
        dateWeek: null,
        startDate: null,
        endDate: null,
      });
      return;
    }
    setState({
      ...state,
      dateValue: dateString,
      dateString: date.format('YYYY-MM-DD'),
      dateWeek: date,
      startDate: date.startOf('day').format('YYYY-MM-DDT00:00:00.000+03:30'),
      endDate: date.endOf('day').format('YYYY-MM-DDT23:59:59.000+03:30'),
    });
  };

  const onChangeDatePicker = (date, dateString) => {
    // console.log(date, dateString);
    // console.log(isJalali);
    setWeeklyMode(true);
    switch (state.sort) {
      case SORT_REALTIME:
        return handle_realtimeDateChange(date, dateString);
      case SORT_DAILY:
        return handle_dailyDateChange(date, dateString);
      case SORT_MONTHLY:
        return handle_monthlyDateChange(date, dateString);
      case SORT_YEARLY:
        return handle_yearlyDateChange(date, dateString);
      default:
        return handle_simpleDateChange(date, dateString);
    }
  };

  const handleNext_PreviousDate = (text) => {
    switch (state.sort) {
      case SORT_REALTIME:
        return handle_realtimeNext_PreviousDate(text);
      case SORT_DAILY:
        return handle_dailyNext_PreviousDate(text);
      case SORT_MONTHLY:
        return handle_monthlyNext_PreviousDate(text);
      case SORT_YEARLY:
        return handle_yearlyNext_PreviousDate(text);
    }
  };

  const toCalendar = (date) => {
    if (!date) return date;
    const d = dayjs.isDayjs(date) ? date : dayjs(date);
    return isJalali ? d.calendar('jalali') : d;
  };

  const handle_realtimeNext_PreviousDate = (text) => {
    const dateWeek = getDateWeekDayjs();
    let updatedDateWeek;
    if (text === 'next') {
      updatedDateWeek = dateWeek.add(1, 'day');
    } else {
      updatedDateWeek = dateWeek.subtract(1, 'day');
    }

    updatedDateWeek = toCalendar(updatedDateWeek);

    const newStartIso = displayToCanonicalIso(
      updatedDateWeek,
      isJalali,
      'start',
    );
    const newEndIso = displayToCanonicalIso(updatedDateWeek, isJalali, 'end');

    setState({
      ...state,
      startDate: newStartIso,
      endDate: newEndIso,
      dateWeek: updatedDateWeek,
    });
  };
  const handle_dailyNext_PreviousDate = (text) => {
    // const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.000[Z]'
    // const start = moment().startOf('day').format(DATE_FORMAT)
    // const end = moment().endOf('day').format(DATE_FORMAT)
    // const startOfWeek = moment().startOf('week').format(DATE_FORMAT)

    // const endOfWeek = moment().endOf('week').format(DATE_FORMAT)
    // setState({ ...state, startDate: startOfWeek, endDate: endOfWeek })

    const startDate = moment(state.startDate);
    const endDate = moment(state.endDate);

    let updatedStartDate;
    let updatedEndDate;
    let updatedDateWeek;

    if (text === 'next') {
      updatedStartDate = startDate.add(1, 'weeks').toISOString();
      updatedEndDate = endDate.add(1, 'weeks').toISOString();
      updatedDateWeek = getDateWeekDayjs().endOf('isoWeek').add(1, 'days');
    } else {
      updatedStartDate = startDate.subtract(1, 'weeks').toISOString();
      updatedEndDate = endDate.subtract(1, 'weeks').toISOString();
      updatedDateWeek = getDateWeekDayjs()
        .startOf('isoWeek')
        .subtract(1, 'days');
    }

    updatedDateWeek = toCalendar(updatedDateWeek);

    setState({
      ...state,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      dateWeek: updatedDateWeek,
    });
  };
  const handle_monthlyNext_PreviousDate = (text) => {
    const startDate = moment(state.startDate);
    const endDate = moment(state.endDate);

    // let isInThisMonthYear = dayjs(endDate)
    //   .add(2, 'day')
    //   .isBetween(dayjs().startOf('month'), dayjs().endOf('month'), null, '[]');

    let isInThisMonthYear = dayjs(endDate)
      .add(2, 'day')
      .isBetween(
        getNow().startOf('month'),
        getDateWeekDayjs().endOf('month'),
        null,
        '[]',
      );

    let updatedStartDate;
    let updatedEndDate;
    let updatedDateWeek;

    if (text === 'next') {
      updatedStartDate = startDate.add(1, 'months').toISOString();
      updatedEndDate = isInThisMonthYear
        ? dayjs().toISOString()
        : endDate.add(1, 'months').toISOString();
      updatedDateWeek = getDateWeekDayjs().endOf('month').add(1, 'days');
    } else {
      updatedStartDate = startDate.subtract(1, 'months').toISOString();
      updatedEndDate = dayjs(endDate)
        .subtract(1, 'months')
        .endOf('month')
        .toISOString();
      updatedDateWeek = getDateWeekDayjs().startOf('month').subtract(1, 'days');
    }

    updatedDateWeek = toCalendar(updatedDateWeek);

    setState({
      ...state,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      dateWeek: updatedDateWeek,
    });
  };
  // const handle_yearlyNext_PreviousDate = (text) => {
  //   const startDate = moment(state.startDate);
  //   const endDate = moment(state.endDate);
  //   const dateWeek = state.dateWeek;

  //   let updatedStartDate;
  //   let updatedEndDate;
  //   let updatedDateWeek;

  //   if (text === 'next') {
  //     updatedStartDate = startDate.add(1, 'year').toISOString();
  //     updatedEndDate = endDate.add(1, 'year').toISOString();
  //     updatedDateWeek = dateWeek.clone().endOf('year').add(1, 'days');
  //   } else {
  //     updatedStartDate = startDate.subtract(1, 'year').toISOString();
  //     // updatedEndDate = endDate.subtract(1, 'year').toISOString();
  //     updatedEndDate = endDate.subtract(1, 'year').endOf('year').toISOString();
  //     updatedDateWeek = dateWeek.clone().startOf('year').subtract(1, 'days');
  //   }
  //   setState({
  //     ...state,
  //     startDate: updatedStartDate,
  //     endDate: updatedEndDate,
  //     dateWeek: updatedDateWeek,
  //   });
  // };

  const handle_yearlyNext_PreviousDate = (text) => {
    const startDate = moment(state.startDate);
    const endDate = moment(state.endDate);

    const isInThisYear = dayjs(endDate)
      .add(2, 'day')
      .isBetween(dayjs().startOf('year'), dayjs().endOf('year'), null, '[]');

    let updatedStartDate;
    let updatedEndDate;
    let updatedDateWeek;

    if (text === 'next') {
      updatedStartDate = startDate.add(1, 'year').toISOString();
      updatedEndDate = endDate.add(1, 'year').toISOString();
      // updatedEndDate = isInThisYear
      //   ? dayjs().toISOString()
      //   : endDate.add(1, 'year').toISOString();
      updatedDateWeek = getDateWeekDayjs().endOf('year').add(1, 'days');
    } else {
      updatedStartDate = startDate.subtract(1, 'year').toISOString();
      updatedEndDate = endDate.subtract(1, 'year').endOf('year').toISOString();

      updatedDateWeek = getDateWeekDayjs().startOf('year').subtract(1, 'days');
    }

    updatedDateWeek = toCalendar(updatedDateWeek);

    setState({
      ...state,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      dateWeek: updatedDateWeek,
    });
  };

  const handleDisableNextDate = () => {
    const currentDate = getNow();
    const displayed = getDateWeekDayjs();
    const displayedCanonicalIso = displayToCanonicalIso(
      displayed,
      isJalali,
      'start',
    );
    const displayedCanonical = displayedCanonicalIso
      ? dayjs(displayedCanonicalIso)
      : null;

    if (!displayedCanonical) {
      return true;
    }

    switch (state.sort) {
      case SORT_REALTIME:
        return currentDate.isSame(displayedCanonical, 'day');
      case SORT_MONTHLY:
        if (isJalali) {
          return currentDate.isSame(displayedCanonical, 'month');
        } else {
          return currentDate.isSame(displayedCanonical, 'month');
        }
      case SORT_YEARLY:
        return currentDate.isSame(displayedCanonical, 'year');
      default:
        return true;
    }
  };

  const handleDisablePreviousDate = () => {
    const creation = dayjs(creationDate);
    const displayed = getDateWeekDayjs();
    switch (state.sort) {
      case SORT_REALTIME:
        return creation.isSame(displayed, 'day');
      case SORT_MONTHLY:
        return creation.isSame(displayed, 'month');
      case SORT_YEARLY:
        return creation.isSame(displayed, 'year');
      default:
        return true;
    }
  };

  const Picker = PowerGeneration_Page
    ? DatePicker.RangePicker
    : handleRangedMode
    ? DatePicker.RangePicker
    : DatePicker;

  if (state.sort == SORT_ALL) return '';

  const endDateMinusOneDay = weeklyMode
    ? moment(state.endDate).format('YYYY-MM-DD')
    : moment(state.endDate)
        // .subtract(1, 'days')
        .format('YYYY-MM-DD');

  const startDateMinusOneDay = weeklyMode
    ? moment(state.startDate)
        // .subtract(1, 'days')
        .format('YYYY-MM-DD')
    : moment(state.startDate).format('YYYY-MM-DD');

  const customWeekStartEndFormat = () => {
    if (state.sort == SORT_REALTIME) {
      return `${moment(state.startDate).format('YYYY-MM-DD')}`;
    } else {
      return `${startDateMinusOneDay} - ${endDateMinusOneDay}`;
    }
  };

  const handleHidden = () => {
    if (PowerGeneration_Page) {
      return true;
    } else if (isJalali) {
      if (state.sort == SORT_REALTIME) {
        return false;
      } else {
        return true;
      }
    } else return false;
  };
  const disabledDate = (current) => {
    if (!current) return false;

    const c = dayjs(current);
    const creation = dayjs(creationDate).startOf('month');
    // const now = dayjs();
    const now = getNow();
    const dayBeforeNow = now;
    if (state.sort === SORT_REALTIME) {
      if (isJalali) {
        return c.isBefore(creation, 'day') || c.isAfter(dayBeforeNow, 'day');
      } else {
        return c.isBefore(creation, 'day') || c.isAfter(now, 'day');
      }
    }
    if (state.sort === SORT_MONTHLY) {
      if (isJalali) {
        return c.isBefore(creation, 'month') || c.isAfter(dayBeforeNow, 'day');
      } else {
        return c.isBefore(creation, 'month') || c.isAfter(now, 'month');
      }
    }
    if (state.sort === SORT_YEARLY) {
      return c.isBefore(creation, 'year') || c.isAfter(now, 'year');
    }
    return false;
  };

  return (
    <>
      <ConfigProvider locale={isJalali ? localeFa : localeEn}>
        <div className="flex items-center gap-2">
          {next_previous_btn && (
            <Button
              hidden={handleHidden()}
              disabled={handleDisablePreviousDate()}
              onClick={() => handleNext_PreviousDate('previous')}
              className="rounded-xl p-1 bg-gray-100 hover:bg-gray-100 text-[#6E6FF7] hover:text-[#6E6FF7] border border-indigo-300 hover:border-[#6E6FF7]"
            >
              {dir === 'ltr' ? (
                <GrFormPrevious size={20} />
              ) : (
                <GrFormNext size={20} />
              )}
            </Button>
          )}
          <Picker
            format={
              ignoreRealtimeMode && state.sort == SORT_DAILY
                ? customWeekStartEndFormat
                : null
            }
            disabled={disable}
            // showToday={false}
            allowClear={false}
            value={state?.dateWeek}
            disabledDate={
              disabledDate
              //   (current) => {
              //   if (pending) {
              //     return true;
              //   } else {
              //     return (
              //       (current && current < moment(creationDate)) ||
              //       (current && current > moment().endOf('day'))
              //     );
              //   }
              // }
            }
            picker={getPickerForSort({
              sort: state.sort,
              powerGenerationPage: PowerGeneration_Page,
            })}
            className={`w-60 ${classNames({
              'analysis-datepicker': alwaysVisible,
              'bg-[#EEF0F2] text-[#5B6268] rounded-lg border-none h-8 w-[230px]':
                logFilter,
              'w-[150px]': dashboardDatePickWidth,
            })}`}
            bordered={!alwaysVisible}
            onChange={onChangeDatePicker}
            open={alwaysVisible}
            popupClassName={classNames('analysis-datepicker-popup ', {
              'analysis-datepicker-popup hidden xl:block': alwaysVisible,
              'no-weekdays': isJalali,
            })}
            locale={
              isJalali
                ? {
                    lang: {
                      locale: 'fa_IR',
                      shortMonths: [
                        'فروردین',
                        'اردیبهشت',
                        'خرداد',
                        'تیر',
                        'مرداد',
                        'شهریور',
                        'مهر',
                        'آبان',
                        'آذر',
                        'دی',
                        'بهمن',
                        'اسفند',
                      ],
                    },
                  }
                : undefined
            }
          />

          {next_previous_btn && (
            <Button
              hidden={handleHidden()}
              disabled={handleDisableNextDate()}
              onClick={() => handleNext_PreviousDate('next')}
              className="rounded-xl p-1 bg-gray-100 hover:bg-gray-100 text-[#6E6FF7] hover:text-[#6E6FF7] border border-indigo-300 hover:border-[#6E6FF7]"
            >
              {dir === 'ltr' ? (
                <GrFormNext size={20} />
              ) : (
                <GrFormPrevious size={20} />
              )}
            </Button>
          )}
        </div>
      </ConfigProvider>
    </>
  );
}

export default DateFilter;
